import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ClipboardList,
  PackageX,
  RefreshCw
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { api } from "../lib/api";
import type { DashboardSummary } from "../lib/types";
import { dateTime, money, number, platformLabel, statusLabel } from "../lib/format";
import { StatCard } from "../components/StatCard";
import { StatusBadge, statusTone } from "../components/StatusBadge";
import { useI18n } from "../lib/i18n";

export function DashboardPage() {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: () => api.get<DashboardSummary>("/api/dashboard/summary")
  });

  if (query.isLoading) {
    return <div className="loading-panel">{t("正在加载运营数据...")}</div>;
  }
  if (query.isError || !query.data) {
    return <div className="error-panel">{t("无法加载工作台数据：")}{query.error?.message}</div>;
  }

  const data = query.data;
  return (
    <div className="dashboard-stack">
      <section className="stat-grid">
        <StatCard label={t("今日订单")} value={number(data.metrics.todayOrders)} hint={t("较昨日持续更新")} icon={ClipboardList} />
        <StatCard label={t("待发货")} value={number(data.metrics.pendingShipment)} hint={t("需要仓库或供应商处理")} tone="amber" icon={PackageX} />
        <StatCard label={t("低库存 SKU")} value={number(data.metrics.lowStock)} hint={t("可用库存低于安全库存")} tone="amber" icon={AlertTriangle} />
        <StatCard label={t("同步错误")} value={number(data.metrics.syncErrors)} hint={t("失败或部分异常的同步任务")} tone="red" icon={RefreshCw} />
      </section>

      <section className="dashboard-grid">
        <div className="panel chart-panel">
          <div className="panel-header">
            <div>
              <h2>{t("近 7 天订单趋势")}</h2>
              <span>{t("按平台拆分，单位为订单数")}</span>
            </div>
          </div>
          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.trend} barGap={6}>
                <CartesianGrid stroke="#e6ebf0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #dbe3ea", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="amazon" name="Amazon" fill="#0f9f8f" radius={[3, 3, 0, 0]} />
                <Bar dataKey="tiktok" name="TikTok Shop" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="shein" name="SHEIN" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <aside className="panel connector-panel">
          <div className="panel-header">
            <div>
              <h2>{t("连接器状态")}</h2>
              <span>{t("平台授权和最近同步状态")}</span>
            </div>
            <a href="/connectors" className="text-link">{t("查看详情")}</a>
          </div>
          <div className="connector-list">
            {data.connectorHealth.map((connector) => (
              <div className="connector-row" key={connector.platform}>
                <div className={`platform-mark platform-${connector.platform}`}>{connector.label.slice(0, 1)}</div>
                <div className="connector-copy">
                  <strong>{connector.label}</strong>
                  <StatusBadge tone={statusTone(connector.status)}>{t(statusLabel(connector.status))}</StatusBadge>
                  <small>{t("店铺数量")} {connector.storeCount} · {t("上次同步")} {dateTime(connector.lastSyncAt)}</small>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </section>

      <section className="dashboard-grid lower-grid">
        <div className="panel table-panel">
          <div className="panel-header">
            <div>
              <h2>{t("最近订单")}</h2>
              <span>{t("最近进入系统的订单")}</span>
            </div>
            <a href="/orders" className="text-link">{t("查看全部")}</a>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("平台")}</th>
                  <th>{t("店铺名称")}</th>
                  <th>{t("订单号")}</th>
                  <th>{t("状态")}</th>
                  <th>{t("金额")}</th>
                  <th>{t("国家")}</th>
                  <th>{t("下单时间")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recentOrders.map((order) => (
                  <tr key={order.id}>
                    <td><span className={`platform-dot dot-${order.platform}`} />{platformLabel(order.platform)}</td>
                    <td>{order.store_name}</td>
                    <td className="mono">{order.platform_order_no}</td>
                    <td><StatusBadge tone={statusTone(order.status)}>{t(statusLabel(order.status))}</StatusBadge></td>
                    <td>{money(order.total_amount, order.currency)}</td>
                    <td>{order.country}</td>
                    <td>{dateTime(order.ordered_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="panel low-stock-panel">
          <div className="panel-header">
            <div>
              <h2>{t("低库存 SKU")}</h2>
              <span>{t("低于安全库存的 SKU")}</span>
            </div>
          </div>
          <div className="compact-list">
            {data.lowStock.map((item) => (
              <div className="compact-row" key={`${item.sku}-${item.warehouse}`}>
                <div>
                  <strong>{item.name}</strong>
                  <span className="mono">{item.sku}</span>
                </div>
                <div className="stock-count">
                  <strong>{item.available}</strong>
                  <small>{t("安全")} {item.safety_stock}</small>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </section>
    </div>
  );
}
